// Single funnel-tracking entry point. Two layers, different granularity:
//   1. Amplitude + GTM - EVERY event, including every quiz step (full funnel).
//   2. First-party `events` (Supabase, direct anon insert) - only the few
//      milestones we need for referral attribution & revshare:
//      ref_visit (daily), quiz_started (start), app_open (dashboard login).
//      Trial/payment are written server-side by the webhook. This keeps the
//      Supabase table small and avoids overload.
// See REFERRALS_AND_ANALYTICS.md.
import sendAnalyticsEvent from './sendAnalyticsEvent';
import { supabase } from '../lib/supabase';
import { getAttributedRef } from './referral';

export type TrackType =
    | 'ref_visit'
    | 'ref_click'
    | 'session_started'
    | 'quiz_started'
    | 'page_completed'
    | 'email_submitted'
    | 'offer_shown'
    | 'checkout_initiated'
    | 'app_open';

// Only these client-side milestones are persisted to Supabase. Everything else
// is Amplitude-only. Must stay a subset of the events_client_insert RLS policy.
const SUPABASE_TYPES: ReadonlySet<TrackType> = new Set<TrackType>([
    'ref_visit',
    'quiz_started',
    'email_submitted',
    'checkout_initiated',
    'app_open',
]);

/**
 * Stable per-browser anonymous id, reused as Amplitude's userId. Created on the
 * first call so even a first landing (before the quiz) is countable as a unique
 * visitor. Stored in localStorage('userId').
 */
export function getOrCreateAnonId(): string | null {
    try {
        let id = localStorage.getItem('userId');
        if (!id) {
            id = (window.crypto && 'randomUUID' in window.crypto)
                ? window.crypto.randomUUID()
                : `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            localStorage.setItem('userId', id);
        }
        return id;
    } catch {
        return null;
    }
}

/** Per-tab/session id (sessionStorage), created on first use. */
function getSessionId(): string | null {
    try {
        let sid = sessionStorage.getItem('sid');
        if (!sid) {
            sid = (window.crypto && 'randomUUID' in window.crypto)
                ? window.crypto.randomUUID()
                : `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            sessionStorage.setItem('sid', sid);
        }
        return sid;
    } catch {
        return null;
    }
}

interface TrackOpts {
    quizId?: string;
    step?: string | number;
    email?: string;
    metadata?: Record<string, any>;
    /** Force a ref code (used by the one-time ref_click). Defaults to the cookie. */
    refCode?: string | null;
}

/**
 * Fire a Meta Pixel (Facebook) event. The base pixel + fbq stub are installed
 * in public/index.html; this is a safe no-op if the pixel hasn't loaded. Never
 * throws - analytics must not break the funnel.
 */
export function trackPixel(
    method: 'track' | 'trackCustom',
    name: string,
    params?: Record<string, any>,
): void {
    try {
        const { fbq } = window as unknown as { fbq?: (...a: any[]) => void };
        if (typeof fbq === 'function') fbq(method, name, params);
    } catch {
        // ignore
    }
}

export function trackEvent(type: TrackType, opts: TrackOpts = {}): void {
    const refCode = opts.refCode !== undefined ? opts.refCode : getAttributedRef();
    const anonymousId = getOrCreateAnonId();
    const email = opts.email || (() => {
        try { return localStorage.getItem('userEmail'); } catch { return null; }
    })();

    // 1. Amplitude / GTM
    sendAnalyticsEvent({
        eventName: type,
        data: {
            quiz_id: opts.quizId,
            step: opts.step,
            ref_code: refCode || undefined,
            ...(opts.metadata || {}),
        },
    });

    // 2. First-party events table - ONLY the milestone types, direct anon
    // insert (fire-and-forget; never blocks the UI). RLS allows insert of these
    // non-sensitive types only.
    if (!SUPABASE_TYPES.has(type)) return;
    try {
        supabase.from('events').insert({
            type,
            anonymous_id: anonymousId || null,
            session_id: getSessionId(),
            email: email || null,
            ref_code: refCode || null,
            quiz_id: opts.quizId || null,
            step: opts.step != null ? String(opts.step) : null,
            metadata: opts.metadata || {},
        }).then(({ error }) => {
            if (error) console.warn('[trackEvent] insert failed:', error.message);
        });
    } catch {
        // ignore
    }
}

export default trackEvent;
